import { IonAlert, IonIcon } from '@ionic/react'
import {
	arrowBack,
	closeOutline,
	ellipsisVertical,
	helpCircle,
	menu as menuIcon,
	refresh,
} from 'ionicons/icons'
import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { AppContext } from '../../contexts/AppContext'
import { ModalContext } from '../../contexts/ModalContext'
import LogoWithText from '../LogoWithText'

export interface AppBarOptions {
	label: string
	beta?: boolean
	onInfoClick?: () => void
	closeFunction?: () => void
	backPath?: string
	ellipsisFunction?: () => void
	onRefetchClick?: () => Promise<void>
	refetchAlertText?: RefetchAlertText
	logo?: boolean
	noPadding?: boolean
	overrideBackgroundStyle?: string
	isModal?: boolean
	navigationType?: 'back' | 'menu' | 'none'
	rightContent?: React.ReactNode
}

interface RefetchAlertText {
	header?: string
	text?: string
	confirmText?: string
	cancelText?: string
}

const AppBar: React.FC<AppBarOptions> = ({
	label,
	beta,
	onInfoClick,
	closeFunction,
	backPath,
	ellipsisFunction,
	onRefetchClick,
	refetchAlertText,
	logo,
	noPadding,
	overrideBackgroundStyle,
	isModal,
	navigationType = 'back',
	rightContent,
}) => {
	const { closeModal, modalStackLength } = useContext(ModalContext)
	const { toggleMenu, isMobileView } = useContext(AppContext)
	const [showRefetchAlert, setShowRefetchAlert] = useState(false)
	const [isRefetching, setIsRefetching] = useState(false)
	const history = useHistory()

	const clickHandler = () => {
		if (closeFunction) return closeFunction()
		if (isModal) return closeModal()
		if (backPath) return history.push(backPath)
		history.goBack()
	}

	const handleRefetchClick = () => {
		setShowRefetchAlert(true)
	}

	const handleRefetch = async () => {
		if (onRefetchClick) {
			setIsRefetching(true)
			setShowRefetchAlert(false)
			try {
				await onRefetchClick()
			} finally {
				setIsRefetching(false)
			}
		}
	}

	return (
		<div
			className={`app-bar ${overrideBackgroundStyle ? overrideBackgroundStyle : 'bg-background-100-light dark:bg-background-100-dark'} text-text-dark dark:text-white h-[5.7rem]${!isModal && !isMobileView && !overrideBackgroundStyle ? ' border-b border-[#c3c3c3] dark:border-[#0d2d30]' : ''}`}
			style={{
				padding: logo ? '0.7rem 0' : noPadding ? '1.4rem 0' : undefined,
			}}
		>
			{logo && (
				<div className='flex w-full items-center justify-center'>
					<LogoWithText size={64} />
				</div>
			)}

			{label !== 'Onboarding' &&
				(isModal ? (
					<>
						{modalStackLength === 0 && (
							<IonIcon onClick={toggleMenu} icon={menuIcon} className='button-hover' />
						)}
						{modalStackLength === 1 && (
							<IonIcon onClick={clickHandler} icon={arrowBack} className='button-hover' />
						)}
						{modalStackLength > 1 && (
							<IonIcon onClick={clickHandler} icon={closeOutline} className='button-hover' />
						)}
					</>
				) : navigationType === 'menu' ? (
					<IonIcon onClick={toggleMenu} icon={menuIcon} className='button-hover' />
				) : navigationType === 'back' ? (
					<IonIcon onClick={clickHandler} icon={arrowBack} className='button-hover' />
				) : null)}

			{!logo && (
				<div className='title'>
					{!logo && <h1>{label}</h1>}
					{beta && <span className='beta-tag ml-2'>BETA</span>}

					<div className='grow' />
				</div>
			)}

			<div className='flex items-center gap-2 xl:gap-4'>
				{rightContent ? (
					rightContent
				) : (
					<>
						{onRefetchClick &&
							refetchAlertText &&
							refetchAlertText.header &&
							refetchAlertText.text &&
							refetchAlertText.cancelText &&
							refetchAlertText.confirmText && (
								<>
									<div className='rotate-45'>
										<div className={`${isRefetching ? 'animate-spin' : ''}`}>
											<IonIcon
												icon={refresh}
												onClick={handleRefetchClick}
												className={`button-hover`}
											/>
										</div>
									</div>
									<IonAlert
										isOpen={showRefetchAlert}
										onDidDismiss={() => setShowRefetchAlert(false)}
										header={refetchAlertText.header}
										message={refetchAlertText.text}
										buttons={[
											{
												text: refetchAlertText.cancelText,
												role: 'cancel',
											},
											{
												text: refetchAlertText.confirmText,
												handler: handleRefetch,
											},
										]}
									/>
								</>
							)}

						{onInfoClick && (
							<IonIcon
								className='text-[#070707] dark:text-white button-hover'
								icon={helpCircle}
								onClick={onInfoClick}
							/>
						)}

						{ellipsisFunction && (
							<IonIcon
								icon={ellipsisVertical}
								onClick={ellipsisFunction}
								className='button-hover'
							/>
						)}
					</>
				)}
			</div>
		</div>
	)
}

export default AppBar
